import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact, formatPercent, formatDate, INVESTMENT_TYPE_LABEL } from "@/lib/format";
import { Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TYPE_COLOR = {
  land: "bg-slate-100 text-slate-800",
  loan: "bg-yellow-50 text-yellow-800",
  company_share: "bg-blue-50 text-blue-800",
  business_franchise: "bg-red-50 text-red-700",
};

export default function Investments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/investments").then((r) => setItems(r.data));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow">Portfolio</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="investments-title">
            <em className="italic font-light">Investments.</em>
          </h1>
          <p className="mt-3 text-sm text-slate-500">All the assets MARKSAM Ventures holds today.</p>
        </div>
        {(user?.role === "admin" || user?.role === "accounts") && (
          <Link
            to="/investments/new"
            data-testid="add-investment-btn"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-red-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Investment
          </Link>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((inv) => (
          <Link
            to={`/investments/${inv.id}`}
            key={inv.id}
            data-testid={`inv-card-${inv.id}`}
            className="border border-slate-200 bg-white p-6 hover-lift"
          >
            <div className="flex items-start justify-between">
              <div>
                <span className={`inline-block text-[10px] tracking-wider uppercase px-2 py-0.5 ${TYPE_COLOR[inv.type]}`}>
                  {INVESTMENT_TYPE_LABEL[inv.type]}
                </span>
                <h3 className="mt-2 font-display font-bold text-xl text-slate-900">{inv.name}</h3>
                <div className="text-xs text-slate-500 mt-1">Invested {formatDate(inv.invested_at)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-wider uppercase text-slate-400">Return</div>
                <div className={`font-mono-fin font-bold text-lg ${inv.return_percent >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {formatPercent(inv.return_percent)}
                </div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2 pt-4 border-t border-slate-100">
              <div>
                <div className="text-[10px] tracking-wider uppercase text-slate-400">Invested</div>
                <div className="font-mono-fin text-sm text-slate-900 mt-0.5">{formatRupeesCompact(inv.total_invested)}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-wider uppercase text-slate-400">Current</div>
                <div className="font-mono-fin text-sm text-slate-900 mt-0.5">{formatRupeesCompact(inv.current_value)}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-wider uppercase text-slate-400">Profit</div>
                <div className={`font-mono-fin text-sm mt-0.5 ${inv.profit >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {formatRupeesCompact(inv.profit)}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
